import { motion } from "framer-motion";
import { Scale, TrendingUp, Shield, Award } from "lucide-react";

const visionPoints = [
  {
    icon: TrendingUp,
    title: "Referenced in investment committees",
    description: "PROVENANCE reports become standard documentation for technology acquisition decisions.",
  },
  {
    icon: Shield,
    title: "Expected by insurers",
    description: "M&A insurance underwriters require PROVENANCE certification for technology asset coverage.",
  },
  {
    icon: Award,
    title: "Quoted in disputes",
    description: "Our methodology becomes the benchmark for technology valuation in legal proceedings.",
  },
];

export const Vision = () => {
  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-card/30 via-cream/5 to-background" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] bg-radial-glow opacity-30" />

      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cream font-medium text-sm uppercase tracking-wider">Our vision</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-6">
            The question that changes everything
          </h2>
        </motion.div>

        {/* Main Vision Statement */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="relative max-w-4xl mx-auto mb-16"
        >
          <div className="rounded-3xl border-2 border-cream/30 bg-card/50 backdrop-blur-xl p-8 md:p-12">
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-cream/10 via-transparent to-cream/10 blur-xl opacity-50" />

            <div className="relative text-center">
              <Scale className="h-16 w-16 text-cream mx-auto mb-6" />
              <blockquote className="font-display text-2xl md:text-4xl font-bold mb-6 leading-tight">
                "Was <span className="text-cream">PROVENANCE</span> involved?"
              </blockquote>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our vision is for this to become a standard question in every technology acquisition. The benchmark for
                technology truth. The mark of due diligence done right.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Vision Points */}
        <div className="grid md:grid-cols-3 gap-8">
          {visionPoints.map((point, idx) => (
            <motion.div
              key={point.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + idx * 0.1 }}
              className="text-center"
            >
              <div className="h-14 w-14 rounded-2xl bg-cream/10 flex items-center justify-center mx-auto mb-4">
                <point.icon className="h-7 w-7 text-cream" />
              </div>
              <h3 className="font-display text-lg font-bold mb-2">{point.title}</h3>
              <p className="text-sm text-muted-foreground">{point.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-lg font-medium text-foreground">
            From the deal room to the boardroom,
            <span className="text-cream"> PROVENANCE </span>becomes synonymous with technology truth.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
